import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, CheckCircle, Copy, AlertCircle } from 'lucide-react';
import api from '../../../services/api';

const SeedTestParticipantTool = () => {
  const [survey, setSurvey] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const runSeed = async () => {
    if (!survey.trim()) {
      toast.error('Survey id is required');
      return;
    }

    setLoading(true);
    setSummary(null);

    try {
      const body = {
        survey: survey.trim(),
        email: email.trim() || undefined,
        pin: pin.trim() || undefined,
      };
      const { data } = await api.post('/api/dev/seed-test-participant/', body);
      setSummary(data);
      toast.success(`Participant ${data.participant.email} ready — ${data.windows_generated} windows generated`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Seed failed: ${detail}`);
      setSummary({ error: detail });
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <UserPlus className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          Seed Test Participant
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Creates a fresh participant wired to the shared SEED-TEST sponsor/study/site and
          scheduled to take the given survey. Reuses infrastructure across runs, generates
          a fresh participant and schedule each time. Blocked in production.
        </p>
      </div>

      <div>
        <label className="label">
          Survey ID <span className="text-danger-500">*</span>
        </label>
        <input
          type="text"
          value={survey}
          onChange={(e) => setSurvey(e.target.value)}
          placeholder="Survey id (the published survey backing your assessment)"
          className="input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">
            Email (optional)
          </label>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Leave blank for a generated seed-*@example.com"
            className="input"
          />
        </div>
        <div>
          <label className="label">
            PIN (optional)
          </label>
          <input
            type="text"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Default: 0000"
            className="input"
          />
        </div>
      </div>

      <button
        onClick={runSeed}
        disabled={loading}
        className="btn btn-primary"
      >
        {loading ? 'Seeding…' : 'Create test participant'}
      </button>

      {summary && !summary.error && (
        <div className="mt-4 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <CheckCircle className="h-4 w-4 text-success-600 dark:text-success-400" />
            Participant ready — {summary.windows_generated} window{summary.windows_generated === 1 ? '' : 's'} generated
          </div>

          <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <CredRow label="Email" value={summary.participant.email} onCopy={copy} />
            <CredRow label="PIN" value={summary.participant.pin} onCopy={copy} mono />
            <CredRow label="Participant ID" value={summary.participant.participant_id} onCopy={copy} mono />
          </div>

          <details className="text-xs text-gray-600 dark:text-gray-400">
            <summary className="cursor-pointer select-none">Infrastructure & IDs</summary>
            <div className="mt-2 pl-2 space-y-1 font-mono">
              <div>Survey: {summary.survey.name} ({summary.survey.id})</div>
              <div>Sponsor: {summary.infrastructure.sponsor_id}</div>
              <div>Study: {summary.infrastructure.study_code} ({summary.infrastructure.study_id})</div>
              <div>Site: {summary.infrastructure.site_code} ({summary.infrastructure.site_id})</div>
              <div>Schedule: {summary.infrastructure.schedule_id}</div>
              <div>ParticipantSchedule: {summary.infrastructure.participant_schedule_id}</div>
              <div>Participant UUID: {summary.participant.uuid}</div>
            </div>
          </details>
        </div>
      )}

      {summary?.error && (
        <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-danger-500/25 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>{summary.error}</div>
        </div>
      )}
    </div>
  );
};

const CredRow = ({ label, value, onCopy, mono }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}: </span>
      <span className={`text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
    <button
      onClick={() => onCopy(value, label)}
      className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
      title={`Copy ${label}`}
    >
      <Copy className="h-4 w-4" />
    </button>
  </div>
);

export default SeedTestParticipantTool;
